<template>
    <div class="d-inline-block">
        <div
            v-bind:class="{ 'formula-error': expression.inError }"
            class="m-1 d-inline-flex text-nowrap formula-expression">
            <template v-if="expressionComponent">
                <component
                    v-bind="expressionBinder"
                    v-bind:is="expressionComponent"
                    v-bind:expression="expression" />
            </template>
            <template v-else>
                <div>expressionType: {{ expression.Type }}</div>
            </template>
        </div>
    </div>
</template>
<script>
    import { EXPRESSION_TYPES } from "../expressionTypes";
    import binary from "./expressions/binary.vue";
    import divide from "./expressions/divide.vue";
    import staticDisplay from "./expressions/static.vue";
    import reference from "./expressions/reference.vue";
    import isEmpty from "./expressions/isEmptyReference.vue";
    import conditional from "./expressions/ifThenElse.vue";
    import binaryFunction from "./expressions/binaryFunction.vue";
    import unaryFunction from "./expressions/unaryFunction.vue";
    import choiceRelational from "./expressions/choiceRelational.vue";
    import attachmentApproved from "./expressions/attachmentApproved.vue";
    import attachmentRejected from "./expressions/attachmentRejected.vue";
    import dateRelational from "./expressions/dateRelational.vue";
    import dateArithmetic from "./expressions/dateArithmetic.vue";
    import dateDifference from "./expressions/dateDifference.vue";
    import exponent from "./expressions/exponent.vue";
    import { ClientDate } from "Util/clientDate";
    import { THOUSAND_SEPARATOR_FORMATTER } from "Util/dataFormatters";
    import { LOGBASE_OPTIONS } from "Formula/metadata.js";
    import multiInstance from "./expressions/multiInstance.vue";
    import envelopeCompleted from "./expressions/envelopeCompleted.vue";

    const expressionDictionary = {
        [EXPRESSION_TYPES.Addition]: { component: binary, binder: { operator: "+" } },
        [EXPRESSION_TYPES.Subtraction]: { component: binary, binder: { operator: "\u2212" } },
        [EXPRESSION_TYPES.Multiplication]: { component: binary, binder: { operator: "\u00d7" } },
        [EXPRESSION_TYPES.Division]: divide, //{ component: binary, binder: { operator: "\u00f7" } },
        [EXPRESSION_TYPES.Constant]: {
            component: staticDisplay,
            binder: {
                getText($app, expression) {
                    let value = expression.ConstantValue === null ? "Constant" : expression.ConstantValue;
                    value = THOUSAND_SEPARATOR_FORMATTER.format(value);
                    return value;
                }
            }
        },
        [EXPRESSION_TYPES.DataFieldReference]: reference,
        [EXPRESSION_TYPES.EqualTo]: { component: binary, binder: { operator: "=" } },
        [EXPRESSION_TYPES.NotEqualTo]: { component: binary, binder: { operator: "\u2260" } },
        [EXPRESSION_TYPES.GreaterThan]: { component: binary, binder: { operator: ">" } },
        [EXPRESSION_TYPES.LessThan]: { component: binary, binder: { operator: "<" } },
        [EXPRESSION_TYPES.GreaterThanOrEqualTo]: { component: binary, binder: { operator: "\u2265" } },
        [EXPRESSION_TYPES.LessThanOrEqualTo]: { component: binary, binder: { operator: "\u2264" } },
        [EXPRESSION_TYPES.And]: { component: binary, binder: { operator: "AND", operatorIsText: true } },
        [EXPRESSION_TYPES.Or]: { component: binary, binder: { operator: "OR", operatorIsText: true } },
        [EXPRESSION_TYPES.Not]: { component: unaryFunction, binder: { functionName: "NOT" } },
        [EXPRESSION_TYPES.BooleanConstant]: {
            component: staticDisplay,
            binder: {
                getText($app, expression) {
                    return Boolean(expression.ConstantValue).toString();
                }
            }
        },
        [EXPRESSION_TYPES.BooleanDataFieldReference]: reference,
        [EXPRESSION_TYPES.AttachmentIsApprovedReference]: attachmentApproved,
        [EXPRESSION_TYPES.AttachmentIsRejectedReference]: attachmentRejected,
        [EXPRESSION_TYPES.ConditionalNumeric]: conditional,
        [EXPRESSION_TYPES.StringEqual]: { component: binary, binder: { operator: "=" } },
        stringEqualIgnoreCase: { component: binary, binder: { operator: "\u2248" } },
        [EXPRESSION_TYPES.StringNotEqual]: { component: binary, binder: { operator: "\u2260" } },
        stringNotEqualIgnoreCase: { component: binary, binder: { operator: "\u2249" } },
        [EXPRESSION_TYPES.StringConstant]: {
            component: staticDisplay,
            binder: {
                getText($app, expression) {
                    return expression.StringConstantValue === null ? "Text" : expression.StringConstantValue;
                }
            }
        },
        [EXPRESSION_TYPES.ConditionalString]: conditional,
        [EXPRESSION_TYPES.ConditionalDate]: conditional,
        [EXPRESSION_TYPES.StringDataFieldReference]: reference,
        [EXPRESSION_TYPES.StringLength]: { component: unaryFunction, binder: { functionName: "LENGTH OF" } },
        [EXPRESSION_TYPES.ChoiceEqual]: choiceRelational,
        [EXPRESSION_TYPES.ChoiceNotEqual]: choiceRelational,
        [EXPRESSION_TYPES.Min]: { component: binaryFunction, binder: { functionName: "MIN" } },
        [EXPRESSION_TYPES.Max]: { component: binaryFunction, binder: { functionName: "MAX" } },
        [EXPRESSION_TYPES.DateConstant]: {
            component: staticDisplay,
            binder: {
                getText($app, expression) {
                    if (expression.DateConstantValue === null) {
                        return "Date";
                    }
                    const date = new ClientDate(
                        $app,
                        expression.DateConstantValue.Year,
                        expression.DateConstantValue.Month,
                        expression.DateConstantValue.Day
                    );
                    return date.toString();
                }
            }
        },
        [EXPRESSION_TYPES.DateDataFieldReference]: reference,
        [EXPRESSION_TYPES.DateRelational]: dateRelational,
        [EXPRESSION_TYPES.DateArithmetic]: dateArithmetic,
        [EXPRESSION_TYPES.Exponent]: exponent,
        [EXPRESSION_TYPES.Logarithm]: {
            component: unaryFunction,
            binder: {
                functionName(exp) {
                    return exp.PropertyDictionary.logbase === LOGBASE_OPTIONS.euler ? "LN" : "LOG";
                }
            }
        },
        [EXPRESSION_TYPES.FormulaReference]: reference,
        [EXPRESSION_TYPES.ConcatenateStrings]: { component: binary, binder: { operator: "+" } },
        [EXPRESSION_TYPES.ProjectNumberReference]: reference,
        [EXPRESSION_TYPES.DataFieldReferenceAsString]: { component: reference, binder: { functionName: "TEXT" } },
        [EXPRESSION_TYPES.ExistsSameMultiInstanceValue]: multiInstance,
        [EXPRESSION_TYPES.CoalesceMultiInstanceValue]: multiInstance,
        [EXPRESSION_TYPES.SumMultiInstanceValue]: multiInstance,
        [EXPRESSION_TYPES.CountTrueMultiInstanceValue]: multiInstance,
        [EXPRESSION_TYPES.CountMatchingTextMultiInstanceValue]: multiInstance,
        [EXPRESSION_TYPES.Round]: { component: unaryFunction, binder: { functionName: "ROUND" } },
        [EXPRESSION_TYPES.IsEmpty]: isEmpty,
        [EXPRESSION_TYPES.EnvelopeCompleted]: envelopeCompleted,
        [EXPRESSION_TYPES.DateDifference]: dateDifference
    };

    export default {
        inject: ["formulaControl"],
        props: {
            expression: { type: Object, required: true }
        },
        data() {
            return {
                isExpressionComponent: true
            };
        },
        computed: {
            expressionComponent() {
                let componentOrOptions = expressionDictionary[this.expression.Type] || null;
                // special cases for different visualization based on properties
                if (
                    this.expression.Type == EXPRESSION_TYPES.StringEqual &&
                    this.expression.PropertyDictionary.ignoreCase === "true"
                ) {
                    componentOrOptions = expressionDictionary["stringEqualIgnoreCase"] || null;
                }
                if (
                    this.expression.Type == EXPRESSION_TYPES.StringNotEqual &&
                    this.expression.PropertyDictionary.ignoreCase === "true"
                ) {
                    componentOrOptions = expressionDictionary["stringNotEqualIgnoreCase"] || null;
                }
                if (componentOrOptions) {
                    return componentOrOptions.component || componentOrOptions;
                }
                return null;
            },
            expressionBinder() {
                let componentOrOptions = expressionDictionary[this.expression.Type] || null;
                // special cases for different visualization based on properties
                if (
                    this.expression.Type == EXPRESSION_TYPES.StringEqual &&
                    this.expression.PropertyDictionary.ignoreCase === "true"
                ) {
                    componentOrOptions = expressionDictionary["stringEqualIgnoreCase"] || null;
                }
                if (
                    this.expression.Type == EXPRESSION_TYPES.StringNotEqual &&
                    this.expression.PropertyDictionary.ignoreCase === "true"
                ) {
                    componentOrOptions = expressionDictionary["stringNotEqualIgnoreCase"] || null;
                }
                if (componentOrOptions.binder) {
                    return componentOrOptions.binder;
                }
                return null;
            },
            children() {
                return this.expression.Children || [];
            }
        },
        mounted() {
            if (!this.expressionComponent) {
                this.formulaControl.registerExpression(this);
            }
        },
        methods: {
            width() {
                return Math.ceil(this.$el.getBoundingClientRect().width);
            },
            wrapGain() {
                if (this.wrap) {
                    return 0;
                }
                if (this.wrapGainOverride) {
                    return this.wrapGainOverride();
                }
                if (this.children.length <= 0) {
                    return 0;
                }
                return this.width() - Math.max(...this.children.map((child) => child.width()));
            }
        }
    };
</script>

<style lang="scss" scoped>
    @use "sass:math";

    @import "./_formula-style-vars.scss";

    .formula-expression {
        border-radius: ($formula-borderRadius) unquote("/") math.div($paren-width, 2);
    }
    .formula-error {
        background-color: rgba(var(--bs-danger-rgb), 0.25);
    }
    %operator-shared {
        margin-left: 0.25rem !important;
        margin-right: 0.25rem !important;
    }
    :deep(.operator) {
        @extend %operator-shared;
        font-size: 1.5rem;
    }
    :deep(.text-operator) {
        @extend %operator-shared;
        font-weight: bold;
    }
</style>
